import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, GraduationCap, DraftingCompass, Users, Briefcase, Check, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader } from '../ui/card';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import SEO from '../SEO';

const WHATSAPP = 'https://wa.me/5561995792667';

const PLANS = [
  {
    id: 'start',
    name: 'BIM START',
    audience: 'Estudantes e iniciantes',
    icon: GraduationCap,
    accent: 'from-emerald-500 to-teal-600',
    ring: 'border-emerald-500/40',
    chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    description: 'Primeiros passos no BIM com acompanhamento próximo para construir uma base sólida em Revit e metodologia.',
    highlights: [
      'Orientação de trilha de estudos personalizada',
      'Fundamentos de Revit e do processo BIM',
      'Apoio na construção de portfólio inicial',
    ],
  },
  {
    id: 'pro',
    name: 'BIM PRO',
    audience: 'Arquitetos, engenheiros e projetistas',
    icon: DraftingCompass,
    accent: 'from-red-500 to-rose-600',
    ring: 'border-red-500/40',
    chip: 'bg-red-500/15 text-red-300 border-red-500/30',
    description: 'Aprofundamento técnico para elevar a produtividade e a qualidade dos seus projetos no dia a dia.',
    highlights: [
      'Boas práticas de modelagem e documentação',
      'Templates, famílias e padronização',
      'Resolução de desafios reais de projeto',
    ],
  },
  {
    id: 'management',
    name: 'BIM MANAGEMENT',
    audience: 'Coordenadores e gestores de empresas e órgãos públicos',
    icon: Users,
    accent: 'from-blue-500 to-cyan-600',
    ring: 'border-blue-500/40',
    chip: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    description: 'Estruturação de fluxos, coordenação e gestão de equipes e projetos BIM em empresas e instituições.',
    highlights: [
      'Coordenação de modelos e gestão de conflitos',
      'BEP, fluxos de trabalho e padrões',
      'Gestão de equipes e implantação BIM',
    ],
  },
  {
    id: 'executive',
    name: 'BIM EXECUTIVE',
    audience: 'Consultores e gestores estratégicos',
    icon: Briefcase,
    accent: 'from-amber-500 to-orange-600',
    ring: 'border-amber-500/40',
    chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    description: 'Visão estratégica para liderar a transformação BIM, com foco em decisão, negócio e resultados.',
    highlights: [
      'Estratégia e maturidade BIM organizacional',
      'Indicadores, ROI e tomada de decisão',
      'Posicionamento como consultor de referência',
    ],
  },
];

const MentorPage = () => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const contact = (plan) =>
    window.open(`${WHATSAPP}?text=${encodeURIComponent(`Olá! Tenho interesse na mentoria ${plan}.`)}`, '_blank');

  return (
    <>
      <SEO
        title="AJ | BIM Mentor - Mentorias BIM Personalizadas"
        description="Mentorias BIM do estudante ao gestor estratégico: BIM Start, BIM Pro, BIM Management e BIM Executive."
        keywords="mentoria BIM, mentoria Revit, coordenação BIM, gestão BIM, consultoria BIM"
      />
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="mentor-hero">
        <img
          src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Mentoria BIM"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/70 via-black/85 to-black" />
        <div className="relative container mx-auto px-4 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 text-sm"
            data-testid="mentor-back-btn"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <div className="max-w-3xl">
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 mb-4 inline-flex items-center gap-1">
              <Compass size={14} /> AJ | BIM Mentor
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Mentoria BIM <span className="text-amber-400">sob medida</span> para cada etapa da sua jornada
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl">
              Acompanhamento personalizado e direto ao ponto — do primeiro contato com o BIM até a liderança
              estratégica em empresas e órgãos públicos.
            </p>
            <Button
              onClick={() => contact('AJ | BIM Mentor')}
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              data-testid="mentor-hero-cta"
            >
              <MessageCircle className="mr-2" size={18} /> Falar com a AJ | BIM
            </Button>
          </div>
        </div>
      </section>

      {/* Categorias */}
      <section className="py-20 bg-black" data-testid="mentor-plans">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Escolha a <span className="text-amber-400">categoria</span> ideal
            </h2>
            <p className="text-gray-400">
              Cada mentoria é pensada para um momento e um objetivo específico. Encontre a que combina com você.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              return (
                <Card
                  key={plan.id}
                  className={`bg-zinc-900 border ${plan.ring} overflow-hidden hover:-translate-y-1 transition-transform duration-300`}
                  data-testid={`mentor-plan-${plan.id}`}
                >
                  <div className={`h-1.5 w-full bg-gradient-to-r ${plan.accent}`} />
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.accent} flex items-center justify-center shadow-lg`}>
                        <Icon size={24} className="text-white" />
                      </div>
                      <span className={`text-[11px] font-semibold uppercase tracking-wide border rounded-full px-3 py-1 ${plan.chip}`}>
                        Mentoria
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mt-4">{plan.name}</h3>
                    <p className="text-sm text-amber-300/90">{plan.audience}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">{plan.description}</p>
                    <ul className="space-y-2 mb-6">
                      {plan.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                          <Check size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
                      <span className="text-sm text-gray-400 italic">Valores e benefícios em breve</span>
                      <Button
                        onClick={() => contact(plan.name)}
                        size="sm"
                        variant="outline"
                        className="border-amber-500/50 text-amber-300 hover:bg-amber-500 hover:text-black"
                        data-testid={`mentor-plan-cta-${plan.id}`}
                      >
                        Tenho interesse
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-14">
            <p className="text-gray-400 mb-4">
              Os valores e benefícios de cada plano serão divulgados em breve. Quer ser avisado(a) e garantir
              condições especiais?
            </p>
            <Button
              onClick={() => contact('AJ | BIM Mentor')}
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              data-testid="mentor-footer-cta"
            >
              <MessageCircle className="mr-2" size={18} /> Falar com a AJ | BIM
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  );
};

export default MentorPage;
